<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import CheckSquareBrokenSvg from '@/assets/svg/check-square-broken-16.svg?component';

import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';

import CheckCircleIcon from '@/assets/svg/checkmark.svg?component';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import { ActivityFeedService } from '@/core/activity-feed/activity-feed.service';
import ActivityFeedMessageItem from '@/components/ActivityFeed/ActivityFeedMessageItem/ActivityFeedMessageItem.vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import type { ConversationIndex } from '@/core/conversations/conversation-index/conversation-index.type';

// Props
// ===============================
defineProps<{
  target: string;
}>();

// Emits
// ===============================
const emit = defineEmits<{
  (e: 'isOpen', value: boolean): void;
}>();

// Services
// ===============================
const activityFeedService = new ActivityFeedService();

const INFINITE_SCROLL_ID = 'activity-feed-infinite-scroll';

// Refs / Reactive Properties
// ===============================
// Interface will be added in ONE-3812.
const isModalOpen = ref(false);
const isLoading = ref(false);
const hasStartedInfiniteScroll = ref(false);
const infiniteScrollControl = reactive({
  underCoolDown: false,
  observer: null as IntersectionObserver | null,
});

// Mark all as read functionality
const isConfirmDialogOpen = ref(false);
const isMarkingAllAsRead = ref(false);

// Computed Properties
// ===============================
const messages = computed(() => activityFeedService.unreadMessages);
const messageCount = computed(() => {
  return activityFeedService.unreadMessageCount;
});
const showsMessageCount = computed(() => {
  return messageCount.value > 0;
});

const hasItems = computed(() => {
  return messages.value.length > 0;
});
const showsEmptyState = computed(() => {
  return !isLoading.value && !hasItems.value;
});
const showsItems = computed(() => {
  return hasItems.value;
});

// Methods
// ===============================
const handleMarkAllAsRead = () => {
  isConfirmDialogOpen.value = true;
};

const confirmMarkAllAsRead = async () => {
  try {
    isMarkingAllAsRead.value = true;
    await activityFeedService.markAllAsRead();
    SnackbarService.success('All conversations marked as read');
    isConfirmDialogOpen.value = false;
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to mark all as read. Please try again.');
  } finally {
    isMarkingAllAsRead.value = false;
  }
};

const fetchMoreMessages = async () => {
  try {
    isLoading.value = true;
    await activityFeedService.loadMoreMessages();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not load messages. Please try again by refreshing the page.');
  } finally {
    isLoading.value = false;
  }
};

const fetchMessages = async () => {
  try {
    isLoading.value = true;
    await activityFeedService.fetchUnreadMessages();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not load messages. Please try again by refreshing the page.');
  } finally {
    isLoading.value = false;
  }
};

const startInfiniteScrollObserver = () => {
  if (infiniteScrollControl.observer) {
    infiniteScrollControl.observer.disconnect();
  }

  infiniteScrollControl.observer = new IntersectionObserver(
    async (entries) => {
      entries.forEach(async (entry) => {
        if (isLoading.value) {
          return;
        }

        if (!entry.isIntersecting) {
          infiniteScrollControl.underCoolDown = false;
        }

        if (infiniteScrollControl.underCoolDown) {
          return;
        }

        if (entry.isIntersecting && !infiniteScrollControl.underCoolDown) {
          await fetchMoreMessages();
          infiniteScrollControl.underCoolDown = true;
        }
      });
    },
    {
      threshold: 0.5,
    },
  );

  const loadMoreElement = document.querySelector(`#${INFINITE_SCROLL_ID}`);
  if (loadMoreElement) {
    infiniteScrollControl.underCoolDown = false;
    infiniteScrollControl.observer.observe(loadMoreElement as Element);
  }
};

const handleMessageClick = (message: ConversationIndex) => {
  // Redirect to the conversation
  location.replace(`/jobs?conversationId=${message.conversationId}`);
};

// Watchers
// ===============================
watch(isModalOpen, (newVal) => {
  if (newVal) {
    hasStartedInfiniteScroll.value = false;
    // Reset the message list and fetch new messages
    activityFeedService.resetMessageList();
    fetchMessages();
  }

  emit('isOpen', newVal);
});

watch(isLoading, (value) => {
  if (!value && hasItems.value && !hasStartedInfiniteScroll.value) {
    startInfiniteScrollObserver();
    hasStartedInfiniteScroll.value = true;
  }
});

// Lifecycle Hooks
// ===============================
onBeforeUnmount(() => {
  if (infiniteScrollControl.observer) {
    infiniteScrollControl.observer.disconnect();
  }
});
</script>

<template>
  <v-menu
    v-model="isModalOpen"
    transition="slide-x-transition"
    location="right top"
    :target="target"
    :offset="28"
    :close-on-content-click="false"
    width="420"
  >
    <!-- Activator -->
    <template #activator="{ props }">
      <div class="cursor-pointer" v-bind="props">
        <slot name="activator" :props="props" />
      </div>
    </template>

    <!-- Content -->
    <div class="h-[calc(100dvh-50px)] w-[420px] rounded-2xl bg-white p-6 shadow-md">
      <!-- Header -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <!-- Displays the activityTitle (e.g., "Activity feed <COUNT>"). -->
          <div class="font-serif text-lg font-black leading-6 text-black">
            Activity feed
            <span
              v-if="showsMessageCount"
              class="ml-1 inline-block rounded-full bg-highlight-50 px-2.5 py-0.5"
            >
              <span class="text-sm font-black leading-[14px] text-highlight-500">
                {{ messageCount }}
              </span>
            </span>
          </div>
          <div
            v-if="showsMessageCount"
            class="flex cursor-pointer items-center gap-2"
            @click="handleMarkAllAsRead"
          >
            <span class="text-xs font-bold leading-[18px] text-shade-880">Mark all as read</span>
            <CheckSquareBrokenSvg class="h-4 w-4 text-shade-880" />
          </div>
        </div>
      </div>

      <!-- Items -->
      <div v-show="showsItems" class="h-[calc(100%-48px)] overflow-y-scroll">
        <div v-for="message in messages" :key="message.conversationId" class="w-full">
          <ActivityFeedMessageItem
            :id="message.conversationId"
            :name="message.proName"
            :message-preview="message.lastMessageText"
            :timestamp="message.lastMessageSentTs"
            @click="handleMessageClick(message)"
          />
        </div>
        <div :id="INFINITE_SCROLL_ID" class="flex h-8 items-center justify-center">
          <SpinnerLoader v-if="isLoading" />
        </div>
      </div>

      <!-- Loading -->
      <div v-show="isLoading && !hasItems" class="flex w-full items-center justify-center">
        <SpinnerLoader class="mt-[250px] h-[68px] w-[68px]"></SpinnerLoader>
      </div>

      <!-- Empty State -->
      <div v-show="showsEmptyState" class="flex w-full flex-col items-center justify-center">
        <div
          class="mb-4 mt-16 flex h-[128px] w-[128px] items-center justify-center rounded-full bg-tint-40"
        >
          <CheckCircleIcon class="scale-[2.3] text-tint-100" />
        </div>
        <div class="text-sm font-bold leading-[21px] text-shade-800">All caught up!</div>
      </div>
    </div>
  </v-menu>

  <!-- Confirmation Dialog -->
  <BaseModal
    v-model="isConfirmDialogOpen"
    title="Mark all as read?"
    subtitle="Are you sure? This will clear all unread messages from your activity feed. This action cannot be undone."
    :show-close-button="true"
    :show-cancel-button="true"
    :show-secondary-button="false"
    cancel-button-text="Cancel"
    submit-button-text="Mark all as read"
    :is-loading="isMarkingAllAsRead"
    @cancel="isConfirmDialogOpen = false"
    @submit="confirmMarkAllAsRead"
  />
</template>
